import type { ElementType } from "react";
import { styled } from "@pigment-css/react";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { Skeleton } from "@acto/taskpane/field-sales-2/components/primitives/Skeleton";

const SKELETON_ROW_COUNT = 5;

export const RowList = styled("ul")(({ theme }) => ({
  margin: 0,
  padding: 0,
  listStyle: "none",
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[12],
}));

export const RowItem = styled("li")<{ clickable?: boolean; dividerInset?: boolean }>(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[16],
  paddingTop: theme.fieldSalesTwo.spacing[12],
  borderTop: `1px solid ${theme.fieldSalesTwo.colors.neutral[200]}`,
  "&:first-of-type": {
    borderTop: "none",
    paddingTop: 0,
  },
  variants: [
    {
      props: { clickable: true },
      style: {
        cursor: "pointer",
        transition: `background-color ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
        "&:hover": { backgroundColor: "rgba(19, 19, 19, 0.04)" },
        "&:active": { backgroundColor: "rgba(19, 19, 19, 0.08)" },
      },
    },
    {
      // For rows with a leading badge: the divider starts at the text column
      // (spacing[72] = badge width + row gap).
      props: { dividerInset: true },
      style: {
        position: "relative",
        borderTop: "none",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: theme.fieldSalesTwo.spacing[72],
          right: 0,
          height: 1,
          background: theme.fieldSalesTwo.colors.neutral[200],
        },
        "&:first-of-type::before": {
          content: "none",
        },
      },
    },
  ],
}));

export const RowButton = styled("button")(({ theme }) => ({
  flex: "1 0 0",
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[16],
  margin: 0,
  padding: 0,
  border: "none",
  background: "none",
  font: "inherit",
  color: "inherit",
  textAlign: "left",
  cursor: "pointer",
}));

export const RowBody = styled("div")(({ theme }) => ({
  flex: "1 0 0",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[2],
}));

export const RowEyebrow = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["14"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["168"],
  color: theme.fieldSalesTwo.colors.neutral[800],
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

export const RowTitle = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["15"],
  fontWeight: theme.fieldSalesTwo.font.weight["520"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["148"],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[900],
  overflowWrap: "anywhere",
}));

export const RowMeta = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["14"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["168"],
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

export const RowValue = styled(RowMeta)(({ theme }) => ({
  paddingTop: theme.fieldSalesTwo.spacing[4],
}));

// Leading metric badge for a row (e.g. a +/- change). Shared by GapList and the product list.
export const RowBadge = styled("div")<{ tone: "positive" | "negative" | "neutral" }>(({ theme }) => ({
  flex: "0 0 auto",
  minWidth: 56,
  height: 52,
  // The badge reads against the row's first two text lines, so it keeps a height of its own and
  // stays put when the text column runs deeper than that.
  alignSelf: "flex-start",
  padding: `0 ${theme.fieldSalesTwo.spacing[10]}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: theme.fieldSalesTwo.radius["16"],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["13"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["120"],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.wide,
  variants: [
    {
      props: { tone: "negative" },
      style: {
        background: theme.fieldSalesTwo.colors.status.error.tint,
        color: theme.fieldSalesTwo.colors.status.error.main,
      },
    },
    {
      props: { tone: "positive" },
      style: {
        background: theme.fieldSalesTwo.colors.status.success.tint,
        color: theme.fieldSalesTwo.colors.status.success.main,
      },
    },
    {
      props: { tone: "neutral" },
      style: {
        background: theme.fieldSalesTwo.colors.neutral[200],
        color: theme.fieldSalesTwo.colors.neutral[800],
      },
    },
  ],
}));

// Trailing affordance for rows that drill down; paddingTop optically centers the icon on the
// row's first text line instead of the whole (possibly multi-line) body.
export const RowChevron = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  alignSelf: "flex-start",
  paddingTop: theme.fieldSalesTwo.spacing[6],
  color: theme.fieldSalesTwo.colors.neutral[700],
  flexShrink: 0,
}));

export const RowListEmpty = styled("p")(({ theme }) => ({
  margin: 0,
  textAlign: "center",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  color: theme.fieldSalesTwo.colors.neutral[600],
}));

/** Placeholder row matching {@link RowItem} */
export function RowSkeleton() {
  return (
    <RowItem dividerInset>
      <Skeleton.Box width={56} height={52} radius={16} />
      <RowBody>
        <Skeleton.Line width="60%" height={15} />
        <Skeleton.Line width={80} height={15} />
      </RowBody>
    </RowItem>
  );
}

export function RowSkeletonList({ count = SKELETON_ROW_COUNT }: { count?: number }) {
  return (
    <RowList role="list" aria-busy="true">
      {Array.from({ length: count }).map((_, index) => (
        <RowSkeleton key={index} />
      ))}
    </RowList>
  );
}

// Rows separated by a standalone Divider, not RowItem's borderTop: a border on the rounded,
// hovered row would be drawn inside the highlight. gap is 0; the row's own paddingBlock sets the
// rhythm. (`dividerInset` serves flat rows, where a border on the row itself is correct.)
export const HoverRowList = styled(RowList)({ gap: 0 });

// Starts at the text column, like the `dividerInset` variant above.
export const InsetRowDivider = styled(Divider)(({ theme }) => ({
  marginLeft: theme.fieldSalesTwo.spacing[72],
  width: `calc(100% - ${theme.fieldSalesTwo.spacing[72]})`,
}));

// Bleeds past the card's text column so the highlight reads as a row, not a box around the content.
export const HoverRowItem = styled(RowItem)(({ theme }) => ({
  paddingBlock: theme.fieldSalesTwo.spacing[12],
  paddingInline: theme.fieldSalesTwo.spacing[12],
  marginInline: `-${theme.fieldSalesTwo.spacing[12]}`,
  borderTop: "none",
  borderRadius: theme.fieldSalesTwo.radius[16],
  "&:first-of-type": {
    paddingTop: theme.fieldSalesTwo.spacing[12],
  },
}));

function HoverRowSkeleton({ Item }: { Item: ElementType }) {
  return (
    <Item>
      <RowBody>
        <RowTitle>
          <Skeleton.Line inline width="55%" height={13} />
        </RowTitle>
        <RowMeta>
          <Skeleton.Line inline width="70%" height={12} />
        </RowMeta>
        <RowValue>
          <Skeleton.Line inline width={72} height={12} />
        </RowValue>
      </RowBody>
    </Item>
  );
}

export function HoverRowSkeletonList({
  count = SKELETON_ROW_COUNT,
  Item = HoverRowItem,
}: {
  count?: number;
  Item?: ElementType;
}) {
  return (
    <HoverRowList role="list" aria-busy="true">
      {Array.from({ length: count }).map((_, index) => (
        <HoverRowSkeleton key={index} Item={Item} />
      ))}
    </HoverRowList>
  );
}
